import { useEffect, useState } from 'react';

export const POLL_MS = 5 * 60 * 1000;

let ready = false;
let forced = false;
const forcedListeners = new Set<() => void>();

/** True once a newer build has been seen: the next navigation is a full page load. */
export function newVersionReady(): boolean {
  return ready;
}

/** True once this build must stop: below a deploy's forced minimum, or a 426 from any call. */
export function forcedUpdateReady(): boolean {
  return forced;
}

/**
 * Marks a forced update as seen. Called from the version poll below, and
 * from `releaseGate.ts`'s fetch wrapper the moment any of the app's own
 * calls gets a 426 back -- whichever happens first wins, so a tab mid-sync
 * notices at its very next call rather than waiting for its next poll.
 */
export function markForcedUpdate(): void {
  if (forced) return;
  forced = true;
  for (const listener of forcedListeners) listener();
}

/** Test-only: `ready` and `forced` are module state with no other way back to false. */
export function resetReleaseStatusForTests(): void {
  ready = false;
  forced = false;
}

export interface VersionInfo {
  id: string | null;
  release: number | null;
  minimum: number | null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** The fields `/version.json` holds, each null when it cannot be read as its type. */
export async function fetchVersion(fetchImpl: typeof fetch = fetch): Promise<VersionInfo> {
  try {
    const response = await fetchImpl('/version.json', { cache: 'no-store' });
    if (response.status === 426) markForcedUpdate();
    if (!response.ok) return { id: null, release: null, minimum: null };
    const body: unknown = await response.json();
    const record = body as { id?: unknown; release?: unknown; minimum?: unknown } | null;
    const id = record?.id;
    return {
      id: typeof id === 'string' && id !== '' ? id : null,
      release: asFiniteNumber(record?.release),
      minimum: asFiniteNumber(record?.minimum),
    };
  } catch (error) {
    // Offline, or a host that answers with a page: not a release signal; the next poll tries again.
    console.debug('version check failed', error);
    return { id: null, release: null, minimum: null };
  }
}

/** The id `/version.json` holds, or null when it cannot be read as one. */
export async function fetchBuildId(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  return (await fetchVersion(fetchImpl)).id;
}

/**
 * Polls `/version.json` on focus, when the tab becomes visible, and every
 * five minutes while it is visible. Never checks on mount: the existing
 * poll cadence is unchanged, since the 426 path above is what carries the
 * urgent case. Returns `newVersion` (the id differs from `ownId`: never
 * reloaded for a person, only offered) and `forced` (`minimum` is above
 * `ownRelease`, or a 426 was seen anywhere: the caller shows the blocking
 * message and reloads at once, agora#528).
 */
export function useReleaseStatus(
  ownId: string,
  ownRelease: number,
  fetchImpl: typeof fetch = fetch,
): { newVersion: boolean; forced: boolean } {
  const [newVersion, setNewVersion] = useState(false);
  const [isForced, setIsForced] = useState(forced);

  useEffect(() => {
    if (forced) setIsForced(true);
    const listener = () => setIsForced(true);
    forcedListeners.add(listener);
    return () => {
      forcedListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      const info = await fetchVersion(fetchImpl);
      if (stopped) return;
      if (info.minimum !== null && info.minimum > ownRelease) markForcedUpdate();
      if (info.id !== null && info.id !== ownId) {
        ready = true;
        setNewVersion(true);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = window.setInterval(onVisible, POLL_MS);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [ownId, ownRelease, fetchImpl]);

  return { newVersion, forced: isForced };
}
